// Per-step mandatory-field validation for the Course Authoring Wizard.
//
// Each validator returns an array of human-readable "missing field" labels.
// Empty array = step is valid and Next button should enable.
//
// We split per step (instead of one global check) so the user sees
// step-local feedback while they're still on that step. The aggregate
// validator at the bottom is used by the final "Generate with AI" gate so
// we don't kick off an LLM run against half-filled inputs.

const isNonEmptyString = (v, min = 1) =>
  typeof v === 'string' && v.trim().length >= min;

const hasAnyNonEmptyLine = (v) => {
  if (Array.isArray(v)) return v.some((line) => isNonEmptyString(line));
  if (typeof v === 'string') {
    return v.split(/\n+/).some((line) => isNonEmptyString(line));
  }
  return false;
};

// Step 1 — Basics
export const validateBasics = (f = {}) => {
  const missing = [];
  if (!isNonEmptyString(f.title, 3)) missing.push('Course Title (min 3 chars)');
  if (!isNonEmptyString(f.description, 10))
    missing.push('Description (min 10 chars)');
  if (!hasAnyNonEmptyLine(f.learningObjectives))
    missing.push('At least 1 Learning Objective');
  return missing;
};

// Step 2 — Target Audience
export const validateAudience = (f = {}) => {
  const missing = [];
  if (!isNonEmptyString(f.organizationType)) missing.push('Organization Type');
  if (!isNonEmptyString(f.competenceLevel)) missing.push('Competence Level');
  if (!isNonEmptyString(f.knowledgeLevel)) missing.push('Knowledge Level');
  if (!isNonEmptyString(f.englishCompetence)) missing.push('English Competence');
  return missing;
};

// Step 3 — Course Structure
export const validateStructure = (f = {}) => {
  const missing = [];
  if (!isNonEmptyString(f.difficulty)) missing.push('Difficulty');
  if (!isNonEmptyString(f.deliveryMode)) missing.push('Delivery Mode');
  if (!isNonEmptyString(f.totalDuration)) missing.push('Total Duration');
  const anyDomain =
    (Array.isArray(f.aiDomains) && f.aiDomains.length > 0) ||
    (Array.isArray(f.industryDomains) && f.industryDomains.length > 0) ||
    (Array.isArray(f.functionalDomains) && f.functionalDomains.length > 0);
  if (!anyDomain)
    missing.push('At least 1 Domain (AI / Industry / Functional)');
  return missing;
};

// Step 4 — Pedagogy & Generate
export const validatePedagogyGenerate = (f = {}) => {
  const missing = [];
  // 2026-07-09 UI/validation alignment: the UI labels Instructor Persona
  // as "(Optional)" (matches how the user thinks about it — a nice-to-have
  // that fine-tunes the AI voice). Previously the validator required 10+
  // chars, so users hit an opaque "Generate" gate even though the UI said
  // the field was optional. Dropped the persona requirement to match the
  // label. Pedagogy remains required — that's the actual signal Stage 1
  // needs to structure the course.
  // pedagogy can be either a string-id (radio value) or an object — both
  // are accepted as long as something is selected.
  const pedagogySelected =
    isNonEmptyString(f.pedagogy) ||
    (f.pedagogy && typeof f.pedagogy === 'object' &&
      (f.pedagogy.id || f.pedagogy.value || f.pedagogy.name));
  if (!pedagogySelected) missing.push('Pedagogy selection');
  return missing;
};

// Aggregate — used by Generate-with-AI and Save-as-Template gates so we
// don't kick off an LLM run / persist a template against half-filled
// inputs. Returns { ok, missingByStep: { basics: [], audience: [], ... } }
export const validateAll = (f = {}) => {
  const missingByStep = {
    basics: validateBasics(f),
    audience: validateAudience(f),
    structure: validateStructure(f),
    pedagogyGenerate: validatePedagogyGenerate(f),
  };
  const ok =
    missingByStep.basics.length === 0 &&
    missingByStep.audience.length === 0 &&
    missingByStep.structure.length === 0 &&
    missingByStep.pedagogyGenerate.length === 0;
  return { ok, missingByStep };
};

// UI helper -- consistent inline hint string + label-mapping. Each step
// renders this directly below the Next button when validation fails.
export const formatMissingHint = (missing) =>
  missing.length === 0
    ? ''
    : `Required to continue: ${missing.join(', ')}.`;
